import React, { useState, useEffect } from "react";
import {
    Table,
    Row,
    Col,
    Button,
    Tooltip,
    Input,
    Form,
    Select,
    Spin
} from "antd";
import { 
    useGetApprovalRequestsQuery,
    useGetApprovalRequestsFiltersQuery,
    useSendReminderApprovalRequestMutation,
    useLazyDownloadApprovalRequestsReportQuery // Changed to lazy query
} from "../../../appRedux/api/approvalRequestsApi";
import { DownloadOutlined, EditOutlined, MedicineBoxOutlined } from "@ant-design/icons";
import eyeIcon from "assets/images/icons/visibility.svg";
import notificationBellIcon from "assets/images/icons/notification.svg";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import CustomPagination from "../../../components/CustomPagination";
import { getRole, getUserInfo, getIsApprover } from "../../../helpers";
import LoadingBar from "../../../components/LoadingBar";
import CustomFilterDropdown from "../../../components/CustomFilterDropdown/CustomFilterDropdown";
import ViewApprovalRequests from "../../../components/ViewApprovalRequests/ViewApprovalRequests";
import ApprovalRequestAddEditDrawer from "../../../components/ApprovalRequestAddEditDrawer/ApprovalRequestAddEditDrawer";
import * as FileSaver from "file-saver"; // Add this import
import { showMessage } from "../../../helpers";
import "./style.css";

const ApprovalRequests = (props) => {
    const [isUserAdmin, setIsUserAdmin] = useState(false);
    const [isUserPartner, setIsUserPartner] = useState(false);
    const [isUserLegal, setIsUserLegal] = useState(false);
    const [isUserLicensor, setIsUserLicensor] = useState(false);
    const [isAdminApprover, setIsAdminApprover] = useState(false);
    const [userDetails, setUserDetails] = useState({});
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filter, setFilter] = useState({});
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [selectedViewItem, setSelectedViewItem] = useState(null);
    // State variables for the drawer
    const [isRaiseEditDrawerVisible, setIsRaiseEditDrawerVisible] = useState(false);
    const [drawerMode, setDrawerMode] = useState('add'); // 'add' or 'edit'
    const [selectedRequestForEdit, setSelectedRequestForEdit] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const [form] = Form.useForm();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    // Send Reminder Mutation
    const [sendReminder, { isLoading: isSendingReminder }] = useSendReminderApprovalRequestMutation();

    // Download Report Lazy Query - matches royalty sales pattern
    const [downloadReport, { isLoading: isDownloading }] = useLazyDownloadApprovalRequestsReportQuery();

    // Fetch filter options from API
    const { 
        data: filtersData, 
        isLoading: filtersLoading, 
        error: filtersError 
    } = useGetApprovalRequestsFiltersQuery();

    // API call for approval requests with dynamic parameters
    const {
        data: approvalRequestsData,
        isLoading: approvalRequestsLoading,
        error: approvalRequestsError,
        refetch: refetchApprovalRequests
    } = useGetApprovalRequestsQuery({
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filter
    }, 
    {
        refetchOnMountOrArgChange: true,
    },
    );

    // Filter options state with proper structure
    const [partnerFilterOptions, setPartnerFilterOptions] = useState([]);
    const [typeFilterOptions, setTypeFilterOptions] = useState([]);
    const [licenseFilterOptions, setLicenseFilterOptions] = useState([]);
    const [statusFilterOptions, setStatusFilterOptions] = useState([]);

    // Process filter data when API response is received
    useEffect(() => {
        if (filtersData) {
            // Process partners - keep full objects for value lookup
            if (filtersData.partners && Array.isArray(filtersData.partners)) {
                setPartnerFilterOptions(filtersData.partners.map(partner => partner.label));
            }

            // Process approval request types - keep full objects for value lookup
            if (filtersData.approval_request_type_options && Array.isArray(filtersData.approval_request_type_options)) {
                setTypeFilterOptions(filtersData.approval_request_type_options.map(type => type.label));
            }

            // Process licenses - keep full objects for value lookup
            if (filtersData.licenses && Array.isArray(filtersData.licenses)) {
                setLicenseFilterOptions(filtersData.licenses.map(license => license.label));
            }

            // Process status options - keep full objects for value lookup
            if (filtersData.status_options && Array.isArray(filtersData.status_options)) {
                setStatusFilterOptions(filtersData.status_options.map(status => status.label));
            }
        }
    }, [filtersData]);

    // Update data and pagination when API response is received
    useEffect(() => {
        if (approvalRequestsData) {
            // Transform API data to match table structure - updated to match actual JSON structure
            const transformedData = approvalRequestsData.results.map((item, index) => ({
                key: item.approval_request_id || index,
                approval_request_id: item.approval_request_id,
                partner: item.partner?.company_name || "N/A",
                requestType: item.approval_request_type,
                requestTypeDisplay: item.approval_request_type_display,
                license: item.license?.name || "N/A",
                licenseData: item.license,
                partyName: item.party_name,
                requestDetails: item.request_details,
                status: item.status,
                statusDisplay: item.status_display,
                partner_comments: item.partner_comments,
                admin_comments: item.admin_comments,
                legal_comments: item.legal_comments,
                licensor_comments: item.licensor_comments,
                approvalDate: item.approval_date,
                requestDate: item.created_at,
                updatedAt: item.updated_at,
                partnerDetails: item.partner,
                legalStatus: item.legal_status,
                licensorStatus: item.licensor_status,
                legalStatusDisplay: item.legal_status_display,
                licensorStatusDisplay: item.licensor_status_display,
                workflowType: item.workflow_type,
                workflowTypeDisplay: item.workflow_type_display,
                documents: item.documents || [],
                isPartnerRevisionRequired : item.is_partner_edit_required || false,
            }));

            setData(transformedData);
            setPagination(prev => ({
                ...prev,
                total: approvalRequestsData.count || 0
            }));
        }
    }, [approvalRequestsData]);

    // Set loading state based on API calls
    useEffect(() => {
        setIsLoading(approvalRequestsLoading);
    }, [approvalRequestsLoading]);

    // Get user role and details at component mount
    useEffect(() => {
        setUserDetails(getUserInfo());
        setIsAdminApprover(getIsApprover());
        setIsUserAdmin(getRole() === "ADMIN");
        setIsUserPartner(getRole() === "PARTNER");
        setIsUserLegal(getRole() === "LEGAL");
        setIsUserLicensor(getRole() === "LICENSOR");
    }, []);

    // Function to get filter value by label for API calls
    const getFilterValueByLabel = (filterType, label) => {
        if (!filtersData) return label;
        
        let result;
        switch (filterType) {
            case 'partner':
                result = filtersData.partners?.find(p => p.label === label);
                return result ? result.value : label;
            
            case 'requestType':
                result = filtersData.approval_request_type_options?.find(rt => rt.label === label);
                return result ? result.value : label;
            
            case 'license':
                result = filtersData.licenses?.find(l => l.label === label);
                return result ? result.value : label;
            
            case 'status':
                result = filtersData.status_options?.find(s => s.label === label); // Find by label
                return result ? result.value : label; // Return the value for BE
            
            default:
                return label;
        }
    };

    // Function to get filter label by value for display
    const getFilterLabelByValue = (filterType, value) => {
        if (!filtersData) return value;
        
        let result;
        switch (filterType) {
            case 'partner':
                result = filtersData.partners?.find(p => p.value === value);
                return result ? result.label : value;
            
            case 'requestType':
                result = filtersData.approval_request_type_options?.find(rt => rt.value === value);
                return result ? result.label : value;
            
            case 'license':
                result = filtersData.licenses?.find(l => l.value === value);
                return result ? result.label : value;
            
            case 'status':
                result = filtersData.status_options?.find(s => s.value === value);
                return result ? result.label : value;
            
            default:
                return value;
        }
    };

    // Function to get filename from file path
    const getFileNameFromPath = (filePath) => {
        if (!filePath) return "Document";
        // Decode URI encoded characters and get the filename
        try {
            const decodedPath = decodeURIComponent(filePath);
            return decodedPath.split('/').pop() || "Document";
        } catch (error) {
            return filePath.split('/').pop() || "Document";
        }
    };

    // Function to show the drawer
    const showDrawer = (item) => {
        setSelectedViewItem(item);
        setIsDrawerVisible(true);
    };

    // Function to close the drawer
    const closeDrawer = () => {
        setIsDrawerVisible(false);
        setSelectedViewItem(null);
    };

    const showRaiseRequestDrawer = () => {
        setDrawerMode('add');
        setSelectedRequestForEdit(null);
        setIsRaiseEditDrawerVisible(true);
    };

    const showEditRequestDrawer = (item) => {
        setDrawerMode('edit');
        setSelectedRequestForEdit(item.approval_request_id);
        setIsRaiseEditDrawerVisible(true);
    };

    // Handle Send Reminder functionality
    const handleSendReminder = async (approvalRequestId) => {
        try {
            await sendReminder(approvalRequestId).unwrap();
            // The success message is handled in the mutation's onQueryStarted
        } catch (error) {
            console.error('Failed to send reminder:', error);
            // The error message is handled in the mutation's onQueryStarted
        }
    };

    // Handle Download Report functionality
    const handleDownloadClick = async () => {
        try {
            // Call downloadReport with empty object or no parameters
            const result = await downloadReport();
            
            if (result.data) {
                // Use FileSaver to save the file
                FileSaver.saveAs(result.data, `approval-requests-report-${new Date().toISOString().split('T')[0]}`);
                showMessage("success", "Approval Requests report downloaded successfully!");
            }
        } catch (error) {
            console.error("Download failed:", error);
            showMessage("error", "Failed to download approval requests report");
        }
    };

    // Get filter values from URL for column configuration
    const getDefaultFilteredValue = (key) => {
        return queryParams.get(key) ? queryParams.getAll(key) : [];
    };

    const handleTableChange = (paginationInfo, filters, sorter) => {
        // Handle pagination changes
        if (paginationInfo) {
            setPagination(prev => ({
                ...prev,
                current: paginationInfo.current || prev.current,
                pageSize: paginationInfo.pageSize || prev.pageSize
            }));
        }

        // Process filters for API call - convert labels to values
        const apiFilters = {};
        
        if (filters) {
            Object.keys(filters).forEach((key) => {
                if (filters[key] && filters[key].length > 0) {
                    // Convert filter labels to values for API call
                    const filterValues = filters[key].map(label => 
                        getFilterValueByLabel(key, label)
                    );
                    
                    // Use correct parameter names for API
                    switch (key) {
                        case 'requestType':
                            apiFilters.requestType = filterValues;
                            break;
                        default:
                            apiFilters[key] = filterValues;
                    }
                }
            });
        }

        // Handle sorting - updated to match your requirements
        if (sorter && sorter.field) {
            let ordering = '';
            
            // Map table field names to API ordering parameters
            const fieldMapping = {
                'requestDate': 'created_at',
                'approvalDate': 'approval_date'
            };
            
            const apiField = fieldMapping[sorter.field] || sorter.field;
            
            if (sorter.order === 'descend') {
                ordering = `-${apiField}`;
            } else if (sorter.order === 'ascend') {
                ordering = apiField;
            }
            
            if (ordering) {
                apiFilters.ordering = ordering;
            }
        }
        
        setFilter(apiFilters);
    };

    const columns = [
        ...(isUserPartner ? [] : [{
            title: "Partner",
            dataIndex: "partner",
            key: "partner",
            align: "center",
            width: "12%",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <CustomFilterDropdown
                    options={partnerFilterOptions}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Partners..."
                    loading={filtersLoading}
                />
            ),
            defaultFilteredValue: getDefaultFilteredValue("partner"),
            filterMultiple: true,
            render: (text) => text,
        }]),
        {
            title: "Type of Request",
            dataIndex: "requestTypeDisplay",
            key: "requestType",
            align: "center",
            width: "14%",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <CustomFilterDropdown
                    options={typeFilterOptions}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Request Types..."
                    loading={filtersLoading}
                />
            ),
            defaultFilteredValue: getDefaultFilteredValue("requestType"),
            filterMultiple: true,
            render: (text, record) => text || getFilterLabelByValue('requestType', record.requestType) || record.requestType,
        },
        {
            title: "License",
            dataIndex: "license",
            key: "license",
            align: "center",
            width: "12%",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <CustomFilterDropdown
                    options={licenseFilterOptions}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Licenses..."
                    loading={filtersLoading}
                />
            ),
            defaultFilteredValue: getDefaultFilteredValue("license"),
            filterMultiple: true,
            render: (text) => text,
        },
        {
            title: "Name of the Party",
            dataIndex: "partyName",
            key: "partyName",
            align: "center",
            width: "14%",
        },
        {
            title: "Request Date",
            dataIndex: "requestDate",
            key: "requestDate",
            align: "center",
            width: "10%",
            defaultSortOrder: "descend",
            sorter: true,
            render: (text) => text ? moment(text).format("DD/MM/YYYY") : "-",
        },
        {
            title: "Approval Date",
            dataIndex: "approvalDate",
            key: "approvalDate",
            align: "center",
            width: "10%",
            sorter: true,
            render: (text) => text ? moment(text).format("DD/MM/YYYY") : "-",
        },
        {
            title: "Documents",
            dataIndex: "documents",
            key: "documents",
            align: "center",
            width: "9%",
            render: (documents, record) =>
                documents && Array.isArray(documents) && documents.length > 0 ? (
                    <Tooltip 
                        title={
                            <div style={{ textAlign: "left" }}>
                                {documents.map((doc, index) => (
                                    <div key={doc.document_id || index}>
                                        {getFileNameFromPath(doc.file)}
                                    </div>
                                ))}
                            </div>
                        }
                        placement="topLeft"
                    >
                        <div style={{ textAlign: "center" }}>
                            {documents.map((doc, index) => (
                                <div key={doc.document_id || index}>
                                    <a
                                        href={doc.file}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ margin: '2px 0', display: 'block' }}
                                    >
                                        Doc {index + 1}
                                    </a>
                                </div>
                            ))}
                        </div>
                    </Tooltip>
                ) : (
                    <span style={{ color: '#999' }}>No documents</span>
                ),
        },
        {
            title: "Status",
            dataIndex: "statusDisplay",
            key: "status",
            align: "center",
            width: "10%",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <CustomFilterDropdown
                    options={statusFilterOptions}
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                    placeholder="Search Status..."
                    loading={filtersLoading}
                />
            ),
            defaultFilteredValue: getDefaultFilteredValue("status"),
            filterMultiple: true,
            render: (text, record) => {
                const statusValue = record.status;
                const statusLabel = text || getFilterLabelByValue('status', statusValue) || statusValue;
                return (
                    <span
                        className="approval-requests-status"
                        style={{
                            color:
                            statusValue === "APPROVED"
                            ? "limegreen"
                            : statusValue === "REJECTED"
                            ? "red"
                            : statusValue === "NEEDS REVISION"
                            ? "#f95b2c"
                            : statusValue === "UNDER PROCESS"
                            ? "orange"
                            : "black",
                        }}
                    >
                        {statusLabel}
                    </span>
                );
            },
        },
        {
            title: "Action",
            key: "operation",
            align: "center",
            width: "10%",
            render: (item) => (
                <div style={{ display: "inline-flex", flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                    {/* View button - visible to all users */}
                    <Button
                        type="link"
                        className="margin-0"
                        onClick={() => showDrawer(item)}
                        id={"view-" + item.key}
                        style={{ padding: 0 }}
                    >
                        <Tooltip title="View request">
                            <img
                                className="icon"
                                style={{ cursor: "pointer" }}
                                src={eyeIcon}
                                alt="eye"
                            />
                        </Tooltip>
                    </Button>

                    {/* Edit button - only for PARTNER users when isPartnerRevisionRequired is True */}
                    {isUserPartner && (
                        <Button
                            type="link"
                            className="margin-0"
                            id={"edit-" + item.key}
                            disabled={!item.isPartnerRevisionRequired}
                            onClick={() => showEditRequestDrawer(item)}
                            style={{ padding: 0 }}
                        >
                            <Tooltip title={"Edit"}>
                                <EditOutlined 
                                    twoToneColor={item.isPartnerRevisionRequired ? "#00AEEF" : "#d9d9d9"} 
                                    className="font-20" 
                                />
                            </Tooltip>
                        </Button>
                    )}

                    {/* Send Reminder button - for ADMIN users */}
                    {isUserAdmin && item.status === "UNDER PROCESS" && (
                        <div className="gx-flex-row gx-align-items-center gx-justify-content-center font-20">
                            <Tooltip title="Send Reminder">
                                <img
                                    className="icon"
                                    src={notificationBellIcon}
                                    alt="notification"
                                    onClick={() => !isSendingReminder && handleSendReminder(item.approval_request_id)}
                                    id={"reminder-" + item.approval_request_id}
                                    style={{ cursor: 'pointer' }}
                                />
                            </Tooltip>
                        </div>
                    )}
                    {isUserPartner && (item.workflowType === null && item.status === "PENDING") && (
                        <div className="gx-flex-row gx-align-items-center gx-justify-content-center font-20">
                            <Tooltip title="Send Reminder">
                                <img
                                    className="icon"
                                    src={notificationBellIcon}
                                    alt="notification"
                                    onClick={() => !isSendingReminder && handleSendReminder(item.approval_request_id)}
                                    id={"reminder-" + item.approval_request_id}
                                    style={{ cursor: 'pointer' }}
                                />
                            </Tooltip>
                        </div>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="approval-requests-page">
            <Row>
                <Col span={24}>
                    <div
                        className="gx-flex-row approval-requests-header"
                        style={{ justifyContent: "space-between" }}
                    >
                        <h1 className="approval-requests-title gx-mb-4">
                            Approval Requests
                        </h1>
                        <div>
                            {isUserPartner && (
                                <Button
                                    type="primary"
                                    id="raise-approval-request"
                                    onClick={showRaiseRequestDrawer}
                                    style={{ marginRight: 8 }}
                                >
                                    <MedicineBoxOutlined className="font-16" />
                                    Raise Approval Request
                                </Button>
                            )}
                            <Tooltip title="Download Report">
                                <Button
                                    onClick={handleDownloadClick}
                                    disabled={isDownloading}
                                    loading={isDownloading}
                                >
                                    <DownloadOutlined className="font-20" />
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                </Col>
                
                <Col span={24}>
                    {isLoading ? (
                        <LoadingBar loading={isLoading} />
                    ) : (
                        <Table
                            className="gx-table-responsive mpp-list-table"
                            columns={columns}
                            dataSource={data}
                            onChange={handleTableChange}
                            bordered
                            scroll={{ y: 'calc(100vh - 250px)', x: "100%" }}
                            style={{ width: "100%" }}
                            pagination={false}
                            sticky={true}
                            loading={approvalRequestsLoading}
                        />
                    )}

                    {/* Custom Pagination */}
                    <CustomPagination
                        current={pagination.current}
                        total={pagination.total}
                        pageSize={pagination.pageSize}
                        onChange={(page, pageSize) => {
                            setPagination(prev => ({
                                ...prev,
                                current: page,
                                pageSize: pageSize
                            }));
                        }}
                        showSizeChanger={true}
                        pageSizeOptions={['10', '20', '50', '100']}
                    />
                </Col>
            </Row>
            <ViewApprovalRequests
                visible={isDrawerVisible}
                onClose={closeDrawer}
                requestData={selectedViewItem}
                isLoading={false}
                refetchData={refetchApprovalRequests}
            />
            <ApprovalRequestAddEditDrawer
                visible={isRaiseEditDrawerVisible}
                onClose={() => setIsRaiseEditDrawerVisible(false)}
                editMode={drawerMode === 'edit'}
                requestId={selectedRequestForEdit}
                onSuccess={() => {
                    refetchApprovalRequests();
                    setIsRaiseEditDrawerVisible(false);
                }}
            />
        </div>
    );
};

export default ApprovalRequests;